/**
 * Quote moderation queue — super_user + admin approve or reject pending
 * quote submissions. Rejections don't delete anything; they only hide
 * the row from the rotation. Every submission is preserved forever.
 */
import { useEffect, useState, useCallback } from 'react';
import api from '../lib/api';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { toast } from 'sonner';
import { CheckCircle, XCircle, Clock } from 'lucide-react';

export default function QuoteApprovalPanel() {
  const [pending, setPending] = useState([]);
  const [busy, setBusy] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await api.get('/quotes/pending');
      setPending(r.data?.quotes || []);
    } catch {
      setPending([]);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const act = async (id, verb) => {
    setBusy(b => ({ ...b, [id]: verb }));
    try {
      await api.post(`/quotes/${id}/${verb}`);
      toast.success(verb === 'approve' ? 'Published to the wall.' : 'Rejected — still preserved in the archive.');
      setPending(p => p.filter(q => q.quote_id !== id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || `Could not ${verb}`);
    } finally {
      setBusy(b => ({ ...b, [id]: null }));
    }
  };

  return (
    <Card className="rounded-2xl border-[#E8E5EC] p-6" data-testid="quote-approval-panel">
      <div className="flex items-baseline justify-between mb-3 gap-2 flex-wrap">
        <div>
          <h3 className="font-serif text-xl text-[#2D2833] flex items-center gap-2">
            <Clock size={16} className="text-[#685B72]" /> Quote moderation queue
          </h3>
          <p className="text-xs text-[#685B72] mt-0.5">
            This table lists every quote submitted by staff that hasn't been published yet. Approving adds it to the Dashboard rotation; rejecting hides it from the wall but the submission is kept in the archive permanently.
          </p>
        </div>
        <div className="text-xs text-[#685B72]">{pending.length} pending</div>
      </div>
      {pending.length === 0 ? (
        <div className="text-sm text-[#685B72] italic py-6" data-testid="quote-approval-empty">
          Nothing waiting for review — you're all caught up.
        </div>
      ) : (
        <ul className="space-y-3">
          {pending.map(q => (
            <li key={q.quote_id} className="rounded-xl border border-[#E8E5EC] p-4 bg-white" data-testid={`quote-pending-${q.quote_id}`}>
              <blockquote className="font-serif text-base text-[#2D2833] leading-relaxed">
                <span className="text-[#BAA9CA]">“</span>{q.text}<span className="text-[#BAA9CA]">”</span>
              </blockquote>
              <div className="flex items-center justify-between mt-3 gap-3 flex-wrap">
                <div className="text-xs text-[#685B72]">
                  — {q.author_name} · <span className="text-[#685B72]">{q.author_email}</span> · submitted{' '}
                  {q.created_at ? new Date(q.created_at).toLocaleDateString() : ''}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!!busy[q.quote_id]}
                    onClick={() => act(q.quote_id, 'reject')}
                    className="rounded-full text-xs border-[#8A3A3A]/40 text-[#8A3A3A] hover:bg-[#8A3A3A]/10"
                    data-testid={`quote-reject-${q.quote_id}`}
                  >
                    <XCircle size={12} className="mr-1" /> Reject
                  </Button>
                  <Button
                    size="sm"
                    disabled={!!busy[q.quote_id]}
                    onClick={() => act(q.quote_id, 'approve')}
                    className="rounded-full text-xs bg-[#839E97] text-white hover:bg-[#839E97]"
                    data-testid={`quote-approve-${q.quote_id}`}
                  >
                    <CheckCircle size={12} className="mr-1" /> Approve
                  </Button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
